export type ContactCardClipboardInput = {
  displayName: string;
  companyName?: string | null;
  professionalTitle?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  profileUrl: string;
};

const present = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Renders the card as plain text for the sender to paste into their own email.
 *
 * Plain text only, by decision on 2026-08-17. The pre-spec had asked for a rich
 * HTML flavour carrying the provider's photo, and rejected a text-only card on
 * the grounds that losing the photo and styling defeats the deliverable — that
 * call was reversed deliberately, not overlooked.
 */
export const buildContactCardText = (card: ContactCardClipboardInput): string => {
  const identity: string[] = [card.displayName];
  const subtitle = [card.professionalTitle, card.companyName].filter(present).join(' · ');
  if (subtitle) identity.push(subtitle);

  // Labelled, and in its own block. Chat apps auto-link both an address and a
  // URL, so an unlabelled email sitting directly above an unlabelled link reads
  // as one long link rather than two separate things you can act on.
  const contact: string[] = [];
  if (present(card.phoneNumber)) contact.push(`Phone: ${card.phoneNumber.trim()}`);
  if (present(card.email)) contact.push(`Email: ${card.email.trim()}`);

  const blocks = [identity.join('\n'), contact.join('\n'), `Profile: ${card.profileUrl}`];

  // A blank line between blocks: the separation is what stops the whole card
  // collapsing into a single run of blue text in a chat bubble.
  return blocks.filter((block) => block.length > 0).join('\n\n');
};

/** Writes the card to the clipboard. Rejects if the browser refuses access. */
export const copyContactCard = async (card: ContactCardClipboardInput): Promise<void> => {
  await navigator.clipboard.writeText(buildContactCardText(card));
};
